/**
 * Number and unit formatting shared by the Results views and the HTML report.
 * All emission masses are carried internally in kg CO₂e and converted only for display.
 */

export const KG_PER_TONNE = 1000;

export const MASS_UNITS = {
  kg: { key: "kg", label: "kg", co2eLabel: "kg CO₂e", fromKg: 1 },
  t: { key: "t", label: "t", co2eLabel: "t CO₂e", fromKg: 1 / KG_PER_TONNE },
};

export const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Converts a kg value to the requested mass unit key ("kg" | "t"). */
export const convertMass = (valueKg, unitKey = "kg") => toFiniteNumber(valueKg) * (MASS_UNITS[unitKey] || MASS_UNITS.kg).fromKg;

/** Picks "t" when the largest absolute value would read better in tonnes. */
export const pickMassUnit = (valuesKg = []) => {
  const max = valuesKg.reduce((m, v) => Math.max(m, Math.abs(toFiniteNumber(v))), 0);
  return max >= KG_PER_TONNE ? "t" : "kg";
};

export const formatNumber = (value, { maximumFractionDigits = 2, minimumFractionDigits = 0, locale } = {}) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "";
  return new Intl.NumberFormat(locale, { maximumFractionDigits, minimumFractionDigits }).format(Number(value));
};

export const formatMass = (valueKg, unitKey = "kg", options) => formatNumber(convertMass(valueKg, unitKey), options);

/** Share of `part` in `total` as a 0-100 number; 0 when total is 0. */
export const shareOf = (part, total) => {
  const t = Math.abs(toFiniteNumber(total));
  return t === 0 ? 0 : (Math.abs(toFiniteNumber(part)) / t) * 100;
};

export const formatPercent = (value, options = { maximumFractionDigits: 1 }) => `${formatNumber(value, options)}%`;

/** Strips characters that are invalid in file names on common OSes and caps the length. */
export const sanitizeFileName = (name, maxLength = 80) =>
  String(name ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, "")
    .replace(/\s+/g, "_")
    .slice(0, maxLength) || "Report";
